import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClinicalAlerts } from '../../../../../redux/modules/patient/actions';
import ClinicalAlertCreate from './ClinicalAlertCreate';
import ClinicalAlertTable from './ClinicalAlertTable';
import ClinicalAlertInformationHeader from './clinical-alert-information-header';

const ClinicalAlerts = ({
  updateClinicalAlerts = () => {},
}) => {
  const { id: patientId } = useParams();
  const dispatch = useDispatch();
  // Check both possible state structures (ClinicalAlerts with capital C and clinicalAlerts with lowercase c)
  const rawAlertsFromStore = useSelector((state) => {
    const alertsState = state.patients?.clinicalAlerts || {};
    return alertsState.ClinicalAlerts || alertsState.clinicalAlerts || [];
  });
  const EMPTY = useMemo(() => [], []);
  const alertsFromStore = Array.isArray(rawAlertsFromStore) ? rawAlertsFromStore : EMPTY;

  // Local list like PatientAllergies mirrors store
  const [allAlerts, setAllAlerts] = useState([]);

  // Auto-select Clinical Alerts if data exists
  const [isActiveAlertPresent, setIsActiveAlertPresent] = useState(null);
  
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [tableFilter, setTableFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [create, setCreate] = useState(true);
  
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "always-visible", label: "Always Visible" },
    { value: "pharmacist-view", label: "Pharmacist View" },
  ];
  
  useEffect(() => {
    if (patientId) {
      dispatch(fetchClinicalAlerts(patientId));
    }
  }, []);

  // Mirror store into local list and set active flag
  useEffect(() => {
    const raw = Array.isArray(alertsFromStore) ? alertsFromStore : EMPTY;
    setAllAlerts(raw);
    // Auto-select "Add Clinical Alert" when alerts exist; otherwise show both radios unselected
    if (raw.length > 0) {
      setIsActiveAlertPresent(true);
    } else {
      setIsActiveAlertPresent(null);
    }
  }, [alertsFromStore]);

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <div className="flex flex-1 h-full">
            <div className="w-full h-full gap-5">
              <div className="flex items-center justify-between mt-2.5 mb-0.5">
                <h4 className="font-bold text-teal-300 text-xs-display">
                  Clinical Alerts
                </h4>
              </div>
              <div className="w-full border-b-[7px] border-gray-20 mb-[20px]"></div>
              
              <div className="flex flex-col h-[590px] gap-6 overflow-scroll overflow-y-auto overflow-x-hidden">
                {/* No Clinical Alerts Option */}
                <div
                  className={`flex justify-between p-6 border rounded ${
                    isActiveAlertPresent === false
                      ? "border-black bg-gray-20"
                      : "border-gray-100"
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      id="no-clinical-alerts"
                      name="no-clinical-alerts"
                      onClick={() => {
                        setIsActiveAlertPresent(false);
                        updateClinicalAlerts([]);
                      }}
                      checked={isActiveAlertPresent === false}
                      type="radio"
                      className="w-[45px] h-[45px] border-gray-100 cursor-pointer form-radio text-REELTeal focus:ring-transparent"
                    />
                    <div className="flex flex-col pl-6">
                      <h3 className="text-xl font-semibold text-gray-800">
                        No Clinical Alerts
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Add Clinical Alert Option */}
                <div
                  className={`flex flex-col gap-6 p-6 border rounded ${
                    isActiveAlertPresent === true
                      ? "border-black bg-gray-20"
                      : "border-gray-100"
                  }`}
                >
                  <ClinicalAlertInformationHeader
                    setIsActiveAlertPresent={setIsActiveAlertPresent}
                    allAlerts={allAlerts}
                    setShowCreateSlider={setShowCreateSlider}
                    isActiveAlertPresent={isActiveAlertPresent}
                    filterOptions={filterOptions}
                    tableFilter={tableFilter}
                    setTableFilter={setTableFilter}
                    alerts={allAlerts}
                  />
                  
                  {/* Show table when Clinical Alerts is selected and there are alerts */}
                  {isActiveAlertPresent === true && allAlerts.length > 0 && (
                    <ClinicalAlertTable
                      clinicalAlerts={allAlerts}
                      tableFilter={tableFilter}
                      setTableFilter={setTableFilter}
                      setShowCreateSlider={setShowCreateSlider}
                      setSelectedAlert={setSelectedAlert}
                      selectedAlert={selectedAlert}
                      setCreate={setCreate}
                      onDeleted={() => {
                        if (patientId) {
                          dispatch(fetchClinicalAlerts(patientId));
                        }
                      }}
                      onUpdated={() => {
                        if (patientId) {
                          dispatch(fetchClinicalAlerts(patientId));
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clinical Alert Slider */}
      <ClinicalAlertCreate
        open={showCreateSlider}
        onClose={() => {
          setShowCreateSlider(false);
          setSelectedAlert(null);
          setCreate(true);
        }}
        selectedAlert={selectedAlert}
        patientId={patientId}
        create={create}
        clinicalAlerts={allAlerts}
        setClinicalAlerts={() => {}}
        onSave={async () => {
          // Refetch from backend to refresh table after save
          if (patientId) {
            await dispatch(fetchClinicalAlerts(patientId));
          }
        }}
        onOpenExistingAlertEdit={(existingAlert) => {
          setSelectedAlert(existingAlert);
          setCreate(false);
          setShowCreateSlider(true);
        }}
      />
    </div>
  );
};

export default ClinicalAlerts;
  